import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useAccountFormContext } from '../../context/useAccountFormContext';
import { Select } from '../../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { FormFields } from '../../AccountForm.meta';
import { useYearAgeOptionsWithRightLabel } from '../ContributionStartYearSelect/useYearAgeOptionsWithRightLabel';
import { getDefaultAssetAllocation } from '../../../../../../helpers';
import { AccountType } from '../../../../../../api/useAccountsOptionsQuery';
import { TaxStatusDTO } from '../../../../../../api/useAccountsQuery';

export const AnnuitizationYear = () => {
  const { control, setValue, watch } = useFormContext<FormFields>();

  const [annuitizationStartAtRetirement, annuityIsAlreadyAnnuitized, accountType, taxStatus] = watch(
    [
      'bankAccount.AnnuitizationStartAtRetirement',
      'bankAccount.AnnuityIsAlreadyAnnuitized',
      'bankAccount.AccountType',
      'bankAccount.TaxStatus',
    ],
  );

  const {
    options: { AnnuityYearOptions, DefaultAssetAllocation },
  } = useAccountFormContext();

  const none = { label: 'None', value: -1 };
  const alreadyAnnuitized = { label: 'Already Annuitized', value: 0 };
  const atRetirement = { label: 'At Retirement', value: 1 };

  // Helper function to update asset allocation when annuitization status changes
  const updateAssetAllocation = (newAnnuitizedValue: boolean) => {
    // Only update if account type is Annuity and we have the necessary data
    if (accountType !== AccountType.Annuity || !DefaultAssetAllocation) {
      return;
    }

    // Get the current asset allocation from form
    const currentAssetAllocation = watch('accountPortfolio.assetAllocationList');

    if (!currentAssetAllocation || currentAssetAllocation.length === 0) {
      return;
    }

    // Get new defaults based on annuitization status
    // Note: For annuities, taxStatus doesn't affect the allocation (only annuityIsAnnuitized does)
    const newDefaults = getDefaultAssetAllocation(DefaultAssetAllocation, {
      AccountType: accountType,
      TaxStatus: taxStatus ?? TaxStatusDTO.PreTax,
      AnnuityIsAnnuitized: newAnnuitizedValue,
    });

    // Update the asset allocation to new defaults
    const updatedAllocationList = currentAssetAllocation.map(item => {
      if (item.taxStatus === null) {
        return {
          ...item,
          assetAllocation: newDefaults.map(asset => ({
            ...asset,
            y: asset.y * 100,
          })),
        };
      }
      return item;
    });

    setValue('accountPortfolio.assetAllocationList', updatedAllocationList);
  };

  const annuityYearOptions = [
    none,
    alreadyAnnuitized,
    atRetirement,
    ...AnnuityYearOptions.map(yearOption => ({
      label: yearOption.toString(),
      value: yearOption,
    })),
  ];

  const options = useYearAgeOptionsWithRightLabel({ options: annuityYearOptions });

  return (
    <>
      <Controller
        control={control}
        name="bankAccount.AnnuitizationStartYear"
        render={({ field: { value, ...field } }) => {
          return (
            <Select
              {...field}
              isPortal
              label="Annuitization Year"
              options={options}
              value={options.find(option => {
                if (annuitizationStartAtRetirement) {
                  return option.value === atRetirement.value;
                }

                if (annuityIsAlreadyAnnuitized) {
                  return option.value === alreadyAnnuitized.value;
                }

                if (value === null) {
                  return option.value === none.value;
                }

                return option.value === value;
              })}
              onChange={opt => {
                switch (opt?.value) {
                  case -1: {
                    setValue('bankAccount.AnnuityIsAnnuitized', false);
                    setValue('bankAccount.AnnuityIsAlreadyAnnuitized', false);
                    setValue('bankAccount.AnnuitizationStartYear', null);
                    setValue('bankAccount.AnnuitizationStartAtRetirement', false);
                    updateAssetAllocation(false);
                    break;
                  }

                  case 0: {
                    setValue('bankAccount.AnnuityIsAnnuitized', true);
                    setValue('bankAccount.AnnuityIsAlreadyAnnuitized', true);
                    setValue('bankAccount.AnnuitizationStartYear', new Date().getFullYear());
                    setValue('bankAccount.AnnuitizationStartAtRetirement', false);
                    updateAssetAllocation(true);
                    break;
                  }

                  case 1: {
                    setValue('bankAccount.AnnuityIsAnnuitized', false);
                    setValue('bankAccount.AnnuityIsAlreadyAnnuitized', false);
                    setValue('bankAccount.AnnuitizationStartYear', null);
                    setValue('bankAccount.AnnuitizationStartAtRetirement', true);
                    updateAssetAllocation(false);
                    break;
                  }

                  default: {
                    setValue('bankAccount.AnnuityIsAnnuitized', true);
                    setValue('bankAccount.AnnuityIsAlreadyAnnuitized', false);
                    setValue('bankAccount.AnnuitizationStartYear', opt?.value || null);
                    setValue('bankAccount.AnnuitizationStartAtRetirement', false);
                    updateAssetAllocation(true);
                  }
                }
              }}
            />
          );
        }}
      />
    </>
  );
};
